import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, CheckCircle2, Rocket } from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { toast } from 'react-hot-toast';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import background from '../assets/bg-1.png';
import { db } from '../config/firebase';

// Firestore collection holding launch sign-ups; export it to CSV from the console / gcloud.
const SUBSCRIBERS_COLLECTION = 'comingSoonSubscribers';

// The form card is always dark, whatever the app theme or the browser's
// light/dark preference (Tailwind's `dark:` follows the OS, not our theme).
const CARD_BG = '#12161f';
const CARD_TEXT = '#f8fafc';
const CARD_TEXT_SECONDARY = '#a3a3a3';

const ComingSoon: React.FC = () => {
    const { colors, theme } = useThemeStore();
    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) {
            toast.error('Please enter a valid email address.');
            return;
        }

        try {
            // Keyed by email so repeat sign-ups overwrite rather than duplicate rows.
            await setDoc(doc(db, SUBSCRIBERS_COLLECTION, encodeURIComponent(normalizedEmail)), {
                email: normalizedEmail,
                source: 'coming-soon',
                subscribedAt: serverTimestamp(),
            });

            setIsSubmitted(true);
            toast.success("You're on the list! We'll notify you soon.");
            setEmail('');
        } catch (error) {
            console.error('Subscription error:', error);
            toast.error('Failed to subscribe. Please try again.');
        }
    };

    return (
        <div className="h-screen flex flex-col relative overflow-hidden" style={{ backgroundColor: theme === 'dark' ? '#121212' : '#f0f2f5' }}>
            <div className="flex-1 flex flex-col lg:flex-row w-full min-h-0">
                {/* Left Column: Branding and Hero Text */}
                <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-start p-16 xl:p-24 relative overflow-hidden h-full" style={{ backgroundColor: theme === 'dark' ? '#1e1e1e' : '#ffffff' }}>

                    {/* Brand Name / Logo */}
                    <div className="absolute top-8 left-8 xl:top-12 xl:left-16 flex items-center gap-3">
                        <img src="/vite.png" alt="Idearify Logo" className="w-12 h-12 rounded-xl object-cover" />
                    </div>

                    <div className="w-full max-w-lg mx-auto relative mt-10">
                        <h1 className="text-5xl xl:text-[4rem] font-bold mb-8 leading-[1.1] tracking-tight" style={{ color: colors.text }}>
                            Something<br />
                            great is<br />
                            <span style={{ color: colors.primary }}>on the way.</span>
                        </h1>

                        {/* Collage Container */}
                        <div className="relative w-full aspect-square mt-12 scale-110 translate-x-4">
                            <img src={background} alt="Background" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>

                {/* Right Column: Content Card */}
                <div className="w-full lg:w-1/2 flex flex-col relative z-20">
                    <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-12 overflow-y-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="w-full max-w-[420px] p-8 rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.1)] border border-gray-800 text-center"
                            style={{ backgroundColor: CARD_BG }}
                        >
                            {/* Mobile Logo */}
                            <div className="text-center mb-6 lg:hidden">
                                <img src="/vite.png" alt="Idearify Logo" className="w-16 h-16 rounded-xl object-cover mx-auto mb-4" />
                                <h1 className="text-3xl font-bold" style={{ color: colors.primary }}>Idearify</h1>
                            </div>

                            {/* Status Icon */}
                            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: `${colors.primary}15`, color: colors.primary }}>
                                <Rocket className="w-8 h-8" />
                            </div>

                            <h2 className="text-2xl font-bold mb-3" style={{ color: CARD_TEXT }}>
                                We're Almost There
                            </h2>

                            <p className="text-[15px] leading-relaxed mb-6" style={{ color: CARD_TEXT_SECONDARY }}>
                                We are currently preparing for our launch. See you soon!
                            </p>

                            <div className="border-t border-gray-700 my-6"></div>

                            {/* Email Notification Form */}
                            {!isSubmitted ? (
                                <form onSubmit={handleSubscribe} className="space-y-3">
                                    <p className="text-sm font-medium text-left" style={{ color: CARD_TEXT }}>
                                        Get notified when we go live:
                                    </p>
                                    <div>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full px-4 py-3.5 rounded-md border text-[16px] focus:outline-none focus:ring-2 border-gray-700 placeholder-gray-500 focus:ring-blue-200 focus:border-blue-500"
                                            style={{ backgroundColor: '#0b0e14', color: CARD_TEXT }}
                                            placeholder="Enter your email address"
                                            required
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="w-full py-3.5 rounded-md font-bold text-lg text-white transition-all hover:opacity-90 flex items-center justify-center gap-2 mt-2"
                                        style={{ backgroundColor: colors.primary }}
                                    >
                                        <Bell className="w-5 h-5" />
                                        Notify Me
                                    </button>
                                </form>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="p-4 rounded-md flex items-center gap-3 text-left"
                                    style={{ backgroundColor: `${colors.primary}10` }}
                                >
                                    <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: colors.primary }} />
                                    <p className="text-sm font-medium" style={{ color: CARD_TEXT }}>
                                        Thank you! We'll notify you as soon as the paperwork is finalized.
                                    </p>
                                </motion.div>
                            )}
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 w-full text-center text-sm py-4 z-30" style={{ color: colors.textSecondary }}>
                <p><b>Idearify</b> © {new Date().getFullYear()} All rights reserved</p>
                <p className="text-xs opacity-70 mt-1">Idearify is currently operated by DNA Entertainment Ltd, acting as an interim holding company for Idearify (company no. 13784763).</p>
            </div>
        </div>
    );
};

export default ComingSoon;